import { useLocalStorageWithSubscription } from "@sundae/react-hooks";
import { useWalletDetails } from "@sundae/wallet-react";
import { useMutation, useQueries, useQuery } from "@tanstack/react-query";
import {
  LOCAL_CANCELLED_ORDERS_SLUG,
  LOCAL_PENDING_ORDERS_SLUG,
} from "../../constants/localStorage.constants";
import { EOpTypename } from "../../types/Operation.types";
import { getBrambleSdk } from "../client/bramble.sdk";
import { FreezerReward, PoolFragmentFragment } from "../generated/stats2.sdk";
import { BrambleTransformer } from "../utils/BrambleTransformer.class";

export interface IPendingOrder {
  date: Date;
  depositMixedQuantityA?: string;
  depositMixedQuantityB?: string;
  prevHash?: string;
  hash: string;
  opType: keyof typeof EOpTypename;
  pool?: PoolFragmentFragment | null;
  quantity?: string;
  rewards?: FreezerReward[];
  rewardsMatured?: FreezerReward[];
  swapQuantityA?: string | null;
  swapQuantityB?: string | null;
  withdrawLq?: string;
}

export const useSubmittedOrdersCache = (address: string) =>
  useLocalStorageWithSubscription<IPendingOrder[]>(`${address}-${LOCAL_PENDING_ORDERS_SLUG}`, []);

export const useCancelledOrdersCache = (address: string) =>
  useLocalStorageWithSubscription<string[]>(`${address}-${LOCAL_CANCELLED_ORDERS_SLUG}`, []);

export const useOperationsHistory = ({
  address,
  pageSize = 30,
}: {
  address: string;
  pageSize?: number;
}) => {
  const fetchPortfolioOrdersCached = useMutation(
    async () => {
      const { fetchOrders } = await getBrambleSdk();
      const {
        portfolio: { orders },
      } = await fetchOrders({ address });

      return BrambleTransformer.transformBrambleOrders(
        orders.filter(({ outcome }) => Boolean(outcome)),
        address
      );
    },
    {
      mutationKey: ["v3Orders", address],
    }
  ).mutateAsync;

  return useQuery(
    ["operationsHistory", address, pageSize],
    async () => {
      if (!address) {
        return { bramble: [], stats: [] };
      }

      return await fetchPortfolioOrdersCached();
    },
    {
      enabled: Boolean(address),
      refetchOnMount: "always",
      refetchOnReconnect: false,
      staleTime: 1000 * 60 * 10, // 10 minutes
    }
  );
};

export const useOperationsOpen = ({
  address,
  enabled = true,
}: {
  address: string;
  enabled?: boolean;
}) => {
  const fetchPortfolioOrdersCached = useMutation(
    async () => {
      const { fetchOrders } = await getBrambleSdk();
      const {
        portfolio: { orders },
      } = await fetchOrders({ address });
      return BrambleTransformer.transformBrambleOpenOrders(orders, address);
    },
    {
      mutationKey: ["v3Orders", address],
    }
  ).mutateAsync;

  return useQuery(
    ["operationsOpen", address],
    async () => {
      return await fetchPortfolioOrdersCached();
    },
    {
      enabled: Boolean(address) && enabled,
      staleTime: 1000 * 60 * 10, // 10 minutes
      refetchInterval: 1000 * 5, // 5 seconds,
    }
  );
};

export const useOpenOrders = (poolIdent?: string, pageSize = 50) => {
  const {
    details: { mainAddress },
  } = useWalletDetails();

  return useQuery(
    ["openOrders", pageSize, poolIdent],
    async () => {
      if (poolIdent) {
        const { fetchPoolOpenOrders } = await getBrambleSdk();
        const {
          pools: {
            byId: {
              orders: { open },
            },
          },
        } = await fetchPoolOpenOrders({ id: poolIdent });

        return BrambleTransformer.transformBrambleOpenOrders(open, mainAddress);
      }
    },
    { enabled: Boolean(poolIdent), refetchOnMount: "always", refetchInterval: 1000 * 3 }
  );
};

export const useOpenOrdersForOrderRoutes = (poolIdents: string[]) => {
  const queriesInfo = useQueries({
    queries: poolIdents.map((ident) => ({
      queryKey: ["order-table", ident, poolIdents],
      queryFn: async () => {
        const { fetchPoolOpenOrders } = await getBrambleSdk();
        const {
          pools: {
            byId: {
              orders: { open },
            },
          },
        } = await fetchPoolOpenOrders({ id: ident });

        return BrambleTransformer.transformBrambleOpenOrders(open);
      },
      refetchOnWindowFocus: false,
      refetchInterval: 1000 * 60 * 1, // 1 Minute
      cacheTime: 1000 * 60 * 1, // 1 Minute
      staleTime: 1000 * 60 * 1, // 1 Minute
    })),
  });
  const ordersQueryInfo = {
    someAreFetching: queriesInfo.some(({ isRefetching }) => isRefetching),
    someAreLoading: queriesInfo.some(({ isLoading }) => isLoading),
    allAreLoaded: queriesInfo.every(({ isLoading, isSuccess }) => !isLoading && isSuccess),
    data: queriesInfo.map(({ data }) => data),
  };

  return ordersQueryInfo;
};

// This query is to showcase the slider on the new homepage and serves as interim solution for v3 until we fully switch to bramble
// We refetch every 30 seconds to keep the data up to date.
export const useBrambleProtocolOrders = () => {
  return useQuery(
    ["brambleProtocolOrders"],
    async () => {
      const { fetchProtocolOrders } = await getBrambleSdk();
      const { orders } = await fetchProtocolOrders();
      return orders;
    },
    {
      refetchInterval: 1000 * 5, // 5 seconds
      staleTime: 1000 * 5, // 5 seconds
    }
  );
};
